import React, { useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdVerifiedUser } from 'react-icons/md';
import {
  AppSelect,
  AppTextarea,
  OutlineButton,
  PrimaryButton,
  StatusBadge,
} from '../../../../components/ui';
import { ASSET_KYC_OPTIONS } from '../../../../data/master/equipmentAssets';
import { toApiStatus } from '../../../../models/_helpers';
import { updateEquipmentKyc } from '../../../../repositories/equipment.repository';

function badgeStatus(status) {
  if (status === 'Verified') return 'verified';
  if (status === 'Rejected') return 'error';
  return 'pending';
}

export function EquipmentKycStatusModal({ isOpen, onClose, asset, onSaved }) {
  const toast = useToast();
  const [status, setStatus] = useState('Pending');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !asset) return;
    setStatus(asset.status || 'Pending');
    setReason(asset.rejectionReason || '');
    setReasonError('');
  }, [isOpen, asset]);

  if (!asset) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (status === 'Rejected') {
      const trimmed = reason.trim();
      if (trimmed.length < 5) {
        setReasonError('Enter a clear rejection reason (min 5 characters)');
        return;
      }
      if (trimmed.length > 500) {
        setReasonError('Max 500 characters');
        return;
      }
    }
    if (asset.id == null) {
      toast({ title: 'Missing equipment id', status: 'error', position: 'top-right' });
      return;
    }

    setSaving(true);
    try {
      await updateEquipmentKyc(asset.id, { kycStatus: toApiStatus(status) });
      toast({
        title: 'KYC status updated',
        description: `${asset.assetId} · ${status}`,
        status: 'success',
        position: 'top-right',
      });
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to update KYC',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const title =
    asset.equipmentDescription || asset.equipmentType || asset.assetId;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pb={2}>
          Update equipment KYC
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack align="stretch" spacing={4}>
            <Box
              p={4}
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              <Flex align="center" gap={3}>
                <Flex
                  w="44px"
                  h="44px"
                  align="center"
                  justify="center"
                  borderRadius="sm"
                  bg="bg.amberTint"
                  flexShrink={0}
                >
                  <MdVerifiedUser size={22} color="#1A1A1A" />
                </Flex>
                <Box minW={0}>
                  <Text fontWeight="700" fontSize="15px" noOfLines={1}>
                    {title}
                  </Text>
                  <Flex align="center" gap={2} mt={1} wrap="wrap">
                    <Text fontSize="13px" fontFamily="mono" color="text.secondary">
                      {asset.assetId}
                    </Text>
                    <StatusBadge status={badgeStatus(asset.status || 'Pending')}>
                      Current: {asset.status || 'Pending'}
                    </StatusBadge>
                  </Flex>
                </Box>
              </Flex>
            </Box>

            <AppSelect
              label="New KYC status"
              isRequired
              value={status}
              onChange={(v) => {
                setStatus(v);
                setReasonError('');
                if (v !== 'Rejected') setReason('');
              }}
              options={ASSET_KYC_OPTIONS}
              helperText="Verified equipment can be recommended on indents."
            />

            {status === 'Rejected' ? (
              <Box
                p={4}
                bg="bg.errorTint"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="status.error"
              >
                <Heading size="xs" mb={1} color="status.error">
                  Rejection reason required
                </Heading>
                <Text fontSize="13px" color="text.secondary" mb={4} lineHeight="1.45">
                  Explain why this equipment KYC is rejected.
                </Text>
                <AppTextarea
                  label="Reason"
                  isRequired
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    setReasonError('');
                  }}
                  placeholder="e.g. Missing fitness certificate / RC mismatch"
                  rows={4}
                  maxLength={500}
                  isInvalid={!!reasonError}
                  errorMessage={reasonError}
                  helperText={`${reason.trim().length}/500`}
                />
              </Box>
            ) : (
              <Box
                p={3}
                bg="bg.amberTint"
                borderRadius="sm"
                borderWidth="1px"
                borderColor="utkarsh.amber"
              >
                <Text fontSize="13px" color="text.primary">
                  {status === 'Verified'
                    ? 'Verified equipment appears in Find & recommend.'
                    : 'Pending keeps the asset awaiting KYC review.'}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter gap={2} borderTopWidth="1px" borderColor="border.subtle">
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>Update KYC</PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
